import { merge } from 'lodash';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { type Clip } from '@air/api/types';

import { GalleryItemType } from '~/components/Gallery/types';
import { useEventSubscription } from '~/hooks/useEventSubscription';
import { updateCentralizedClipAssetsAction } from '~/store/centralizedClip/actions';
import { type EventSubscriptionArgs } from '~/types/sockets';
import { useUpdateAssetInAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { getBoardIdFromPath, getClipIdFromPath } from '~/utils/PathUtils';

export type UseHandlePublicClipMediaUpdatedProps = {
  workspaceId: string | undefined;
} & ({ clipIds: string[] } | { clipId: string | RegExp | undefined });

export const useHandleClipMediaUpdated = (props: UseHandlePublicClipMediaUpdatedProps) => {
  const { workspaceId } = props;
  const dispatch = useDispatch();
  const { updateAssetInAllViews } = useUpdateAssetInAllViews();

  const event = useMemo<EventSubscriptionArgs<'System.Clip.Media.updated'> | undefined>(() => {
    if ('clipIds' in props) {
      return props.clipIds.length > 0 ? { eventType: 'System.Clip.Media.updated' } : undefined;
    }

    return props.clipId
      ? {
          eventType: 'System.Clip.Media.updated',
          pattern: {
            clipId: props.clipId,
          },
        }
      : undefined;
  }, [props]);

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event,
    options: {
      onUpdate: ({ data: { updates: assets, clipId } }) => {
        if ('clipIds' in props && !props.clipIds.includes(clipId)) {
          return;
        }

        const boardId = getBoardIdFromPath(window.location.pathname);

        /**
         * When clip's assets have been updated (in this workspace),
         * update it all views (gallery, table, kanban)
         */
        updateAssetInAllViews({
          asset: { id: clipId },
          parentBoardId: boardId,
          type: GalleryItemType.asset,
          updateFn: (existingAsset: Clip) => merge(existingAsset, { assets }),
        });

        const currentClipId = getClipIdFromPath(window.location.pathname);

        /**
         * If it's the current clip the user is looking at,
         * updated centralized clip's assets too
         * The reason we check clipId instead of clip.id
         * is because sometimes clip doesn't have id
         */
        if (currentClipId === clipId) {
          dispatch(
            updateCentralizedClipAssetsAction({
              assets,
            }),
          );
        }
      },
    },
  });
};
